export const LabGenerator = {
    /**
     * Standard laboratory parameters with reference ranges.
     */
    PARAMETERS: {
        Hb: { name: "Hemoglobina", unit: "g/dL", range: [12, 16] },
        Ht: { name: "Hematocrito", unit: "%", range: [36, 48] },
        Leuc: { name: "Leucocitos", unit: "x10^3/µL", range: [4.5, 11] },
        Plat: { name: "Plaquetas", unit: "x10^3/µL", range: [150, 450] },
        Glu: { name: "Glucosa", unit: "mg/dL", range: [70, 100] },
        Cr: { name: "Creatinina", unit: "mg/dL", range: [0.7, 1.3] },
        Na: { name: "Sodio", unit: "mEq/L", range: [135, 145] },
        K: { name: "Potasio", unit: "mEq/L", range: [3.5, 5.0] },
        Cl: { name: "Cloro", unit: "mEq/L", range: [98, 107] },
        BUN: { name: "Urea (BUN)", unit: "mg/dL", range: [7, 20] },
        pH: { name: "pH Arterial", unit: "", range: [7.35, 7.45] },
        pCO2: { name: "pCO2", unit: "mmHg", range: [35, 45] },
        pO2: { name: "pO2", unit: "mmHg", range: [80, 100] },
        HCO3: { name: "HCO3", unit: "mEq/L", range: [22, 26] },
        Lac: { name: "Lactato", unit: "mmol/L", range: [0.5, 2.2] }
    },

    /**
     * Predefined theme playlists (Fallbacks for variety).
     */
    PLAYLISTS: {
        "noche_sepsis": {
            name: "Noche de Sepsis",
            tags: ["sepsis", "choque", "infectologia"],
            description: "Múltiples ingresos con inestabilidad hemodinámica y foco infeccioso."
        },
        "turno_trauma": {
            name: "Turno de Trauma",
            tags: ["trauma", "atls", "urgencias"],
            description: "Ingresos por accidentes y trauma de alta energía."
        },
        "alerta_cardio": {
            name: "Alerta Cardiovascular",
            tags: ["cardio", "iam", "tep", "acls"],
            description: "Prioridad en arritmias y síndromes coronarios."
        },
        "cuidado_quirurgico": {
            name: "Abdomen Quirúrgico",
            tags: ["abdomen_agudo", "cirugia", "gastro_quirurgico"],
            description: "Evaluación de abdomen agudo y probables perforaciones."
        }
    },

    /**
     * Generates a realistic set of lab values based on a theme.
     * @param {string} theme - The theme key.
     * @param {Object} fatigueBias - Optional bias based on player weakness.
     */
    generateProfile(theme, fatigueBias = {}) {
        const values = {};
        const base = this.PLAYLISTS[theme] || { tags: [] };

        // Default: Randomize all parameters slightly around normal
        for (const [key, param] of Object.entries(this.PARAMETERS)) {
            const mid = (param.range[0] + param.range[1]) / 2;
            const range = param.range[1] - param.range[0];
            values[key] = parseFloat((mid + (Math.random() - 0.5) * range).toFixed(1));
        }

        // Apply theme-specific alterations
        if (theme === 'noche_sepsis') {
            values.Leuc = parseFloat((15 + Math.random() * 10).toFixed(1)); // Leukocytosis
            values.Lac = parseFloat((2.5 + Math.random() * 5).toFixed(1)); // High Lactate
            values.HCO3 = parseFloat((15 + Math.random() * 5).toFixed(1)); // Low Bicarb
            values.pH = parseFloat((7.25 + Math.random() * 0.1).toFixed(2)); // Acidosis
        } else if (theme === 'turno_trauma') {
            values.Hb = parseFloat((7 + Math.random() * 4).toFixed(1)); // Anemia
            values.Ht = parseFloat((21 + Math.random() * 12).toFixed(1));
            values.Lac = parseFloat((3 + Math.random() * 3).toFixed(1));
        }

        // Apply fatigue bias (Cognitive Curve)
        for (const [key, multiplier] of Object.entries(fatigueBias)) {
            if (values[key]) values[key] *= multiplier;
        }

        return values;
    },

    /**
     * Returns a shift theme based on player weaknesses.
     * @param {Array} weakTags - Tags where the player has failed.
     */
    getRecommendedTheme(weakTags) {
        if (!weakTags || weakTags.length === 0) {
            const keys = Object.keys(this.PLAYLISTS);
            return keys[Math.floor(Math.random() * keys.length)];
        }
        
        // Find playlist that matches most weak tags
        let bestMatch = "noche_sepsis";
        let maxOverlap = -1;

        for (const [key, playlist] of Object.entries(this.PLAYLISTS)) {
            const overlap = playlist.tags.filter(t => weakTags.includes(t)).length;
            if (overlap > maxOverlap) {
                maxOverlap = overlap;
                bestMatch = key;
            }
        }
        return bestMatch;
    },

    /**
     * Generates an HTML string containing a table of lab results.
     */
    generateHTML(values, options = {}) {
        let rows = "";
        for (const [key, value] of Object.entries(values)) {
            const param = this.PARAMETERS[key];
            if (!param) continue;

            const isLow = value < param.range[0];
            const isHigh = value > param.range[1];
            const statusLabel = isLow ? "↓" : isHigh ? "↑" : "";

            rows += `
                <tr>
                    <td style="padding: 4px 8px; border-bottom: 1px solid rgba(0,0,0,0.1)">${param.name}</td>
                    <td style="padding: 4px 8px; border-bottom: 1px solid rgba(0,0,0,0.1); font-weight: bold; ${isLow || isHigh ? 'color: var(--accent)' : 'color: var(--primary)'}">
                        ${value} ${statusLabel}
                    </td>
                    <td style="padding: 4px 8px; border-bottom: 1px solid rgba(0,0,0,0.1); font-size: 0.8em; opacity: 0.7">
                        ${param.range[0]} - ${param.range[1]} ${param.unit}
                    </td>
                </tr>
            `;
        }

        return `
            <div class="lab-report" style="background: var(--bg); border: 2.5px solid var(--ink); border-radius: var(--radius-hard); margin: 15px 0; overflow: hidden; box-shadow: var(--glow);">
                <div style="background: var(--ink); color: var(--bg); padding: 5px 10px; font-size: 0.8rem; text-transform: uppercase; letter-spacing: 1px; display: flex; justify-content: space-between;">
                    <span>Reporte de Laboratorio</span>
                    <span style="opacity:0.7; font-size:0.7rem;">ID: ${options.nodeId ? options.nodeId.slice(-6).toUpperCase() : 'CORE-' + Math.floor(Math.random() * 999)}</span>
                </div>
                <table style="width: 100%; border-collapse: collapse; text-align: left; font-size: 0.9rem;">
                    <thead>
                        <tr style="background: rgba(0,0,0,0.05)">
                            <th style="padding: 6px 8px;">Parámetro</th>
                            <th style="padding: 6px 8px;">Resultado</th>
                            <th style="padding: 6px 8px;">Ref.</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${rows}
                    </tbody>
                </table>
            </div>
        `;
    }
};
